<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxButton, EcxTooltip } from '@ecx/ui'
import { ArrowRightStrokeIcon, InfoIcon, DocumentDownloadIcon } from '@ecx/ui/icons'
import { useBulkImportStore } from '@/stores/bulkImport'
import BulkImportInstructionsBox from '../shared/BulkImportInstructionsBox.vue'
import folderManual from '@/assets/bulkImport/folder-manual.svg'
import folderSpreadsheet from '@/assets/bulkImport/folder-spreadsheet.svg'

const props = withDefaults(
  defineProps<{
    canImport?: boolean
  }>(),
  {
    canImport: true,
  },
)

const emit = defineEmits<{
  (e: 'select-manual'): void
}>()

const { t } = useI18n()
const store = useBulkImportStore()
const isVehicles = computed(() => store.entity === 'vehicles')
</script>

<template>
  <div class="flex flex-col pb-6">
    <div class="px-6 pt-6 pb-0 flex items-center justify-between">
      <span class="text-base text-grey-950">{{ t('corp.bulk_import.step_selector.subtitle') }}</span>
      <EcxButton variant="outline" size="sm" class="shrink-0 whitespace-nowrap" @click="store.downloadTemplate()">
        <template #iconLeft>
          <DocumentDownloadIcon class="w-4 h-4 text-royal-500" aria-hidden="true" />
        </template>
        {{ t('corp.bulk_import.step_selector.download_template') }}
      </EcxButton>
    </div>

    <div
      class="group mx-6 mt-4 p-5 rounded-lg border border-grey-100 bg-white cursor-pointer hover:border-royal-500 flex items-center gap-5 transition-colors duration-150"
      data-testid="manual-card"
      role="button"
      tabindex="0"
      @click="emit('select-manual')"
      @keydown.enter.prevent="emit('select-manual')"
      @keydown.space.prevent="emit('select-manual')"
    >
      <img :src="folderManual" alt="" class="w-16 shrink-0" />
      <div class="flex-1 flex flex-col gap-1">
        <p class="text-sm text-grey-950">
          {{ t('corp.bulk_import.step_selector.manual_title') }}
        </p>
        <p class="text-label-xs text-grey-600">
          {{ isVehicles ? t('corp.bulk_import.step_selector.manual_desc_vehicles') : t('corp.bulk_import.step_selector.manual_desc') }}
        </p>
      </div>
      <ArrowRightStrokeIcon class="w-6 h-6 text-grey-400 group-hover:text-royal-500 shrink-0" aria-hidden="true" />
    </div>

    <!-- O wrapper mx-6 mt-4 garante largura total dentro do EcxTooltip (inline-flex) -->
    <div class="mx-6 mt-4">
      <EcxTooltip
        class="w-full"
        :content="!props.canImport ? t('corp.bulk_import.permission.bulk_denied_tooltip') : ''"
        :disabled="props.canImport"
        placement="top"
      >
      <div
        class="group w-full p-5 rounded-lg flex items-center gap-5 transition-colors duration-150"
        :class="props.canImport
          ? 'border border-grey-100 bg-white cursor-pointer hover:border-royal-500'
          : 'border border-grey-200 bg-grey-50 cursor-not-allowed opacity-60'"
        data-testid="bulk-card"
        role="button"
        :tabindex="props.canImport ? 0 : -1"
        :aria-disabled="!props.canImport"
        @click="props.canImport ? store.chooseBulk() : undefined"
        @keydown.enter.prevent="props.canImport ? store.chooseBulk() : undefined"
        @keydown.space.prevent="props.canImport ? store.chooseBulk() : undefined"
      >
        <img :src="folderSpreadsheet" alt="" class="w-16 shrink-0" />
        <div class="flex-1 flex flex-col gap-1">
          <p class="text-sm" :class="props.canImport ? 'text-grey-950' : 'text-grey-600'">
            {{ t('corp.bulk_import.step_selector.bulk_title') }}
          </p>
          <p class="text-label-xs text-grey-600">
            {{ isVehicles ? t('corp.bulk_import.step_selector.bulk_desc_vehicles') : t('corp.bulk_import.step_selector.bulk_desc') }}<br />{{ t('corp.bulk_import.step_selector.use_model') }}
          </p>
          <div class="inline-flex items-center gap-1 bg-royal-25 rounded-lg pl-1.5 pr-2 py-1 w-fit">
            <InfoIcon class="w-3 h-3 text-royal-500 shrink-0" aria-hidden="true" />
            <span class="text-label-xs text-royal-500">{{ t('corp.bulk_import.step_selector.supported_formats') }}</span>
          </div>
        </div>
        <ArrowRightStrokeIcon
          class="w-6 h-6 shrink-0 transition-colors duration-150"
          :class="props.canImport ? 'text-grey-400 group-hover:text-royal-500' : 'text-grey-400'"
          aria-hidden="true"
        />
      </div>
      </EcxTooltip>
    </div>

    <BulkImportInstructionsBox />
  </div>
</template>
